import { useEffect, useRef, useCallback, type ReactNode } from "react";
import { createPortal } from "react-dom";
import styles from "./Modal.module.css";

interface ModalProps {
  children: ReactNode;
  onClose: () => void;
  /** Prevent closing (e.g. during async operations) */
  preventClose?: boolean;
  /** aria-labelledby id */
  labelledBy?: string;
  /** Additional class for the modal panel */
  className?: string;
  /** Additional class for the overlay backdrop */
  overlayClassName?: string;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Accessible modal overlay with:
 * - Focus trap (Tab / Shift+Tab cycle)
 * - Escape to close
 * - Click-outside to close
 * - Body scroll lock
 * - aria-modal + role="dialog"
 * - Restores focus on unmount
 */
export function Modal({
  children,
  onClose,
  preventClose = false,
  labelledBy,
  className,
  overlayClassName,
}: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<Element | null>(null);

  const close = useCallback(() => {
    if (!preventClose) onClose();
  }, [onClose, preventClose]);

  // Body scroll lock + inert siblings + focus save/restore.
  // Combined into a single effect so cleanup runs in the correct order:
  // 1) restore inert state (so the trigger element is focusable again)
  // 2) restore focus to the previously active element
  useEffect(() => {
    const savedActiveElement = document.activeElement;
    previousActiveElement.current = savedActiveElement;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Mark all direct children of body (except our portal container) as inert,
    // but remember which ones were already inert so we can restore correctly
    // when modals are stacked.
    const siblings = Array.from(document.body.children).filter(
      (el) => el !== overlayRef.current?.parentElement && el !== overlayRef.current,
    );
    const previouslyInert = new Set(siblings.filter((el) => el.hasAttribute("inert")));
    siblings.forEach((el) => el.setAttribute("inert", ""));

    // Focus first focusable element inside modal, or the modal itself
    const modal = modalRef.current;
    if (modal) {
      const first = modal.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (first) {
        first.focus();
      } else {
        modal.focus();
      }
    }

    return () => {
      // Restore inert FIRST so the trigger element becomes focusable
      document.body.style.overflow = prevOverflow;
      siblings.forEach((el) => {
        if (!previouslyInert.has(el)) {
          el.removeAttribute("inert");
        }
      });
      // Now restore focus
      if (previousActiveElement.current instanceof HTMLElement) {
        previousActiveElement.current.focus();
      }
    };
  }, []);

  // Keyboard: Escape + focus trap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
        return;
      }

      if (e.key === "Tab") {
        const modal = modalRef.current;
        if (!modal) return;

        const focusable = Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [close]);

  // Click outside
  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      close();
    }
  };

  return createPortal(
    <div
      ref={overlayRef}
      className={`${styles.overlay} ${overlayClassName ?? ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onMouseDown={handleOverlayMouseDown}
    >
      <div ref={modalRef} className={`${styles.modal} ${className ?? ""}`} tabIndex={-1}>
        {children}
      </div>
    </div>,
    document.body,
  );
}
